import type { CacheManagerContract, CacheStats, ScopedCacheManager, ManagedCache, CacheOptions } from '@gzl10/nexus-sdk'
import type { EventEmitter2 } from 'eventemitter2'
import type { Redis } from 'ioredis'
import { ManagedCacheImpl } from './managed-cache.js'
import { RedisManagedCache } from './redis-managed-cache.js'
import { ScopedCacheManagerImpl } from './scoped-cache-manager.js'

interface InvalidationBinding {
  event: string
  handler: () => void | Promise<void>
}

/** Minimal logger interface to avoid coupling to pino */
export interface CacheLogger {
  debug: (obj: Record<string, unknown>, msg: string) => void
  info: (obj: Record<string, unknown>, msg: string) => void
  warn: (obj: Record<string, unknown>, msg: string) => void
}

export interface CacheManagerOptions {
  maxCachesPerScope?: number
  adapter?: 'memory' | 'redis'
  redisClient?: Redis
  redisPrefix?: string
}

export class CacheManager implements CacheManagerContract {
  private caches = new Map<string, ManagedCache<any>>()
  private rules = new Map<string, InvalidationBinding[]>()
  private scopeRegistry = new Map<string, Set<string>>()
  private listeners: InvalidationBinding[] = []
  private pruneInterval?: ReturnType<typeof setInterval>
  private defaultMaxCachesPerScope: number
  private adapterType: 'memory' | 'redis'
  private redisClient?: Redis
  private _redisPrefix: string

  constructor(
    private emitter: EventEmitter2,
    private logger?: CacheLogger,
    options?: CacheManagerOptions
  ) {
    this.defaultMaxCachesPerScope = options?.maxCachesPerScope ?? 20
    this.adapterType = options?.adapter ?? 'memory'
    this.redisClient = options?.redisClient
    this._redisPrefix = options?.redisPrefix ?? 'nexus'

    if (this.adapterType === 'redis' && !this.redisClient) {
      throw new Error('redisClient is required when adapter is "redis"')
    }
  }

  /** Redis client (if using Redis adapter). Used by diagnostics. */
  get redis(): Redis | undefined {
    return this.redisClient
  }

  scoped(scope: string, options?: { maxCaches?: number }): ScopedCacheManager {
    return new ScopedCacheManagerImpl(
      scope,
      this,
      options?.maxCaches ?? this.defaultMaxCachesPerScope,
      this.logger
    )
  }

  /** Factory method — creates the appropriate cache implementation */
  createCache<T>(name: string, options?: CacheOptions, onAddRules?: (events: string[]) => void): ManagedCache<T> {
    if (this.adapterType === 'redis' && this.redisClient) {
      return new RedisManagedCache<T>(name, this.redisClient, this._redisPrefix, options, onAddRules)
    }
    return new ManagedCacheImpl<T>(name, options, onAddRules)
  }

  /** @internal — called by ScopedCacheManager */
  register<T>(fullName: string, scope: string, cache: ManagedCache<T>, invalidateOn?: string[]): void {
    this.caches.set(fullName, cache)

    if (!this.scopeRegistry.has(scope)) {
      this.scopeRegistry.set(scope, new Set())
    }
    this.scopeRegistry.get(scope)!.add(fullName)

    if (invalidateOn?.length) {
      this.bindRules(fullName, cache, invalidateOn)
    }

    this.logger?.debug({ cache: fullName, scope, invalidateOn }, 'Cache registered')
  }

  /** @internal — add rules to existing cache */
  addRulesForCache<T>(fullName: string, cache: ManagedCache<T>, events: string[]): void {
    this.bindRules(fullName, cache, events)
  }

  async unregister(fullName: string): Promise<void> {
    const bindings = this.rules.get(fullName)
    if (bindings) {
      for (const { event, handler } of bindings) {
        this.emitter.off(event, handler)
      }
      this.listeners = this.listeners.filter(
        l => !bindings.some(b => b.event === l.event && b.handler === l.handler)
      )
      this.rules.delete(fullName)
    }

    await this.caches.get(fullName)?.clear()
    this.caches.delete(fullName)

    for (const [, names] of this.scopeRegistry) {
      names.delete(fullName)
    }

    this.logger?.debug({ cache: fullName }, 'Cache unregistered')
  }

  get<T = unknown>(fullName: string): ManagedCache<T> | undefined {
    return this.caches.get(fullName) as ManagedCache<T> | undefined
  }

  stats(): Record<string, CacheStats> {
    const result: Record<string, CacheStats> = {}
    for (const [name, cache] of this.caches) {
      result[name] = cache.getStats()
    }
    return result
  }

  async invalidate(eventPattern: string): Promise<void> {
    for (const [fullName, bindings] of this.rules) {
      if (bindings.some(b => b.event === eventPattern)) {
        await this.caches.get(fullName)?.clear()
        this.logger?.debug({ cache: fullName, event: eventPattern }, 'Cache invalidated')
      }
    }
  }

  startPruneScheduler(intervalMs = 300_000): void {
    if (this.pruneInterval) clearInterval(this.pruneInterval)

    const scopeEntries = () => [...this.scopeRegistry.keys()]
    let index = 0

    this.pruneInterval = setInterval(() => {
      const scopes = scopeEntries()
      if (scopes.length === 0) return
      const scope = scopes[index % scopes.length]!
      for (const fullName of this.scopeRegistry.get(scope) ?? []) {
        this.caches.get(fullName)?.prune().catch(() => {})
      }
      index++
    }, intervalMs)
  }

  async destroy(): Promise<void> {
    const count = this.caches.size
    for (const { event, handler } of this.listeners) {
      this.emitter.off(event, handler)
    }
    if (this.pruneInterval) clearInterval(this.pruneInterval)
    for (const cache of this.caches.values()) await cache.clear()
    this.caches.clear()
    this.rules.clear()
    this.scopeRegistry.clear()
    this.listeners = []
    this.logger?.info({ caches: count }, 'CacheManager destroyed')
  }

  private bindRules<T>(fullName: string, cache: ManagedCache<T>, events: string[]): void {
    const existing = this.rules.get(fullName) ?? []
    const newBindings: InvalidationBinding[] = []

    for (const event of events) {
      if (existing.some(b => b.event === event)) continue

      const handler = async () => {
        try {
          await cache.clear()
          this.logger?.debug({ cache: fullName, event }, 'Cache cleared by event')
        } catch (err) {
          this.logger?.warn({ cache: fullName, event, err }, 'Failed to clear cache by event')
        }
      }
      this.emitter.on(event, handler)
      newBindings.push({ event, handler })
      this.listeners.push({ event, handler })
    }

    this.rules.set(fullName, [...existing, ...newBindings])
  }
}
